import React, {memo, useMemo, lazy, Suspense} from 'react'
import {useSelector} from 'react-redux'
import {matchPath, useLocation} from 'react-router-dom'

import appConfig from 'configs/app.config'
import {Loading} from 'components/shared'
import {
    LAYOUT_TYPE_CLASSIC,
    LAYOUT_TYPE_MODERN,
    LAYOUT_TYPE_SIMPLE,
    LAYOUT_TYPE_STACKED_SIDE,
    LAYOUT_TYPE_DECKED,
} from 'constants/theme.constant'
import useDirection from 'utils/hooks/useDirection'
import {getIsAuthUser} from 'store/auth/selectors'
import {getLayoutType} from 'store/theme/selectors'
import {ErrorBoundary} from 'components/shared'
import {publicRoutes} from 'configs/routes.config'

const layouts = {
    [LAYOUT_TYPE_CLASSIC]: lazy(() => import('./ClassicLayout')),
    [LAYOUT_TYPE_MODERN]: lazy(() => import('./ModernLayout')),
    [LAYOUT_TYPE_STACKED_SIDE]: lazy(() => import('./StackedSideLayout')),
    [LAYOUT_TYPE_SIMPLE]: lazy(() => import('./SimpleLayout')),
    [LAYOUT_TYPE_DECKED]: lazy(() => import('./DeckedLayout')),
}

const Layout = () => {
    const location = useLocation()
    const layoutType = useSelector(getLayoutType)
    const authenticated = useSelector(getIsAuthUser)

    useDirection()

    const AppLayout = useMemo(() => {
        // Match either an exact external path OR a dynamic deep-link path
        // whose prefix is registered as an external route. E.g. the prefix
        // `/workout` matches both `/workout` and `/workout/123`, but does
        // NOT match `/workouts-management/123` (different prefix).
        const isExternalPath = appConfig.externalRoutes.some((route) => {
            return location.pathname === route || location.pathname.startsWith(`${route}/`)
        })
        if (isExternalPath) {
            return lazy(() => import('./ExternalRoutesLayout'))
        }

        if (publicRoutes.some((route) => matchPath({path: route.path, end: true}, location.pathname))) {
            return lazy(() => import('./AuthLayout'))
        }

        if (authenticated) {
            return layouts[layoutType]
        }
        return lazy(() => import('./AuthLayout'))
    }, [layoutType, authenticated, location.pathname])

    return (
        <Suspense
            fallback={
                <div className="flex flex-auto flex-col h-[100vh]">
                    <Loading loading={true} />
                </div>
            }
        >
            <ErrorBoundary locationKey={location.key}>
                <AppLayout />
            </ErrorBoundary>
        </Suspense>
    )
}

export default memo(Layout)
